// Personalized discount prices for the jgd-personal-price block.
//
// Source of truth are the cookies `gjwt_<variantId>` (personal price) and
// `gjwt_o<variantId>` (compare-at price). They are written before first paint
// by the app embed "Google-Preis (pv2)" (app `amb-gad`), which reads the
// Google price token from the URL; the discount itself is applied in cart and
// checkout by that app's Shopify Function. This block only DISPLAYS.
//
// The synchronous inline script in the block already decided the first-paint
// state (standard / personal-from-cookie). This module covers variant
// switches: adopt the freshly rendered standard price from the section
// fragment and re-decide the state for the new variant BEFORE the fragment is
// painted, so the price never visibly flips.
//
// 2026-09-27: the former app proxy `/apps/gjwt` (app `amb-google-discounts`)
// is gone — the app was uninstalled. The proxy fetch, the "pending" state and
// the one-time discount code (`/discount/<code>`) were removed with it. Without
// a cookie the standard price is simply shown; nothing is requested.
//
// Plan & rationale: docs/plans/2026-07-02-jgd-personal-price.md

import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';

/**
 * @typedef {Object} JgdPersonalPriceRefs
 * @property {HTMLElement} frame
 * @property {HTMLElement} personalPrice
 * @property {HTMLElement} personalAmount
 * @property {HTMLElement} [personalCompareGroup]
 * @property {HTMLElement} [personalCompare]
 * @property {HTMLElement} [shippingPolicyNote]
 * @property {HTMLElement} [freeShippingNote]
 *
 * @extends {Component<JgdPersonalPriceRefs>}
 */
class JgdPersonalPrice extends Component {
  /** @type {Element | null} */
  #section = null;

  connectedCallback() {
    super.connectedCallback();
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);

    // Failsafe for markup that still carries the legacy "pending" state
    // (e.g. a cached page): resolve it from the cookie right away.
    if (this.dataset.state === 'pending') {
      this.removeAttribute('aria-busy');
      this.#decide();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#section?.removeEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate);
    this.#section = null;
  }

  /**
   * @param {string} name
   * @returns {string | null}
   */
  #readCookie(name) {
    const match = document.cookie.match(`(?:^|; )${name}=([^;]*)`);
    return match ? decodeURIComponent(match[1]) : null;
  }

  /**
   * Parses a cookie money value ("12.34" or "12,34") into minor units.
   * @param {unknown} value
   * @returns {number | null}
   */
  #minorUnits(value) {
    if (value === null || value === undefined || value === '') return null;
    const minor = convertMoneyToMinorUnits(String(value), this.dataset.currency || 'EUR');
    return minor === null || Number.isNaN(minor) ? null : minor;
  }

  /**
   * @param {number} minorUnits
   * @returns {string}
   */
  #format(minorUnits) {
    return formatMoney(minorUnits, this.dataset.moneyFormat || '{{amount}}', this.dataset.currency || 'EUR');
  }

  /**
   * Flips the shipping note between the stock policy text and the
   * free-shipping note, based on the price the visitor actually sees.
   * No-op unless the block rendered both notes (opt-in setting + a known
   * threshold from the shipping-rates metaobject).
   * @param {number | null} minorUnits
   */
  #updateShippingNote(minorUnits) {
    const threshold = Number.parseInt(this.dataset.freeShippingThreshold ?? '', 10);
    const { shippingPolicyNote, freeShippingNote } = this.refs;
    if (
      !Number.isFinite(threshold) ||
      threshold <= 0 ||
      minorUnits === null ||
      !(shippingPolicyNote instanceof HTMLElement) ||
      !(freeShippingNote instanceof HTMLElement)
    ) {
      return;
    }
    const free = minorUnits >= threshold;
    shippingPolicyNote.toggleAttribute('hidden', free);
    freeShippingNote.toggleAttribute('hidden', !free);
  }

  /**
   * Shows the personalized price (and compare-at price, when higher).
   * @param {number} minorUnits
   * @param {number | null} compareMinorUnits
   */
  #applyPersonal(minorUnits, compareMinorUnits) {
    const { personalAmount, personalCompare, personalCompareGroup } = this.refs;
    if (!(personalAmount instanceof HTMLElement)) return this.#showStandard();

    personalAmount.textContent = this.#format(minorUnits);

    if (
      compareMinorUnits !== null &&
      compareMinorUnits > minorUnits &&
      personalCompare instanceof HTMLElement &&
      personalCompareGroup instanceof HTMLElement
    ) {
      personalCompare.textContent = this.#format(compareMinorUnits);
      personalCompareGroup.removeAttribute('hidden');
    } else if (personalCompareGroup instanceof HTMLElement) {
      personalCompareGroup.setAttribute('hidden', '');
    }

    this.dataset.state = 'personal';
    this.#updateShippingNote(minorUnits);
  }

  /**
   * Falls back to the server-rendered standard price.
   */
  #showStandard() {
    delete this.dataset.state;
    this.#updateShippingNote(this.#minorUnits(this.dataset.price));
  }

  /**
   * Decides the state for the current variant from its cookie: personal
   * price when present, standard price otherwise. Synchronous, no network.
   */
  #decide() {
    if (this.hasAttribute('data-design-mode')) return this.#showStandard();

    const variantId = this.dataset.variantId;
    const cookieMinor = variantId ? this.#minorUnits(this.#readCookie(`gjwt_${variantId}`)) : null;

    if (variantId && cookieMinor !== null) {
      this.#applyPersonal(cookieMinor, this.#minorUnits(this.#readCookie(`gjwt_o${variantId}`)));
    } else {
      this.#showStandard();
    }
  }

  /**
   * Adopts the freshly rendered block from the section fragment and decides
   * the state for the new variant — synchronously, before the fragment paints.
   * @param {import('@theme/events').VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    const { data } = event.detail;

    if (data.newProduct) {
      this.dataset.productId = String(data.newProduct.id);
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const next = data.html.querySelector(`jgd-personal-price[data-block-id="${this.dataset.blockId}"]`);
    if (!(next instanceof HTMLElement)) return;

    this.dataset.variantId = next.dataset.variantId || '';
    this.dataset.price = next.dataset.price || '';

    const currentStandard = this.querySelector('.jgd-personal-price__standard');
    const nextStandard = next.querySelector('.jgd-personal-price__standard');
    if (currentStandard && nextStandard) currentStandard.replaceWith(nextStandard);

    this.#decide();
  };
}

if (!customElements.get('jgd-personal-price')) {
  customElements.define('jgd-personal-price', JgdPersonalPrice);
}
